/**
 * businessHealthMockData.ts — Datos demo de "Salud del negocio".
 *
 * QUÉ HACE: construye un `BusinessHealthData` determinista reutilizando los
 *   mocks ya existentes (categorías/top productos y ventas por día de la semana)
 *   para que el panel se vea completo cuando el negocio aún no tiene ventas reales.
 *
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React.
 *
 * Exporta: BUSINESS_HEALTH_MOCK
 */
import { CATEGORY_PERIOD_DATA } from "./categoryMockData";
import { WEEKDAY_SALES_MOCK_DATA } from "./weekdaySalesMockData";
import type {
  BusinessHealthData,
  HealthBarDatum,
  HealthTreemapSlice,
} from "../types/businessHealth.types";

const MAX_TREEMAP_SLICES = 7;
const MAX_RANKING_ITEMS = 6;

/** Treemap demo: unidades vendidas por categoría del último mes. */
function buildTreemap(): HealthTreemapSlice[] {
  return CATEGORY_PERIOD_DATA.mes.categories
    .filter((slice) => slice.value > 0)
    .slice(0, MAX_TREEMAP_SLICES)
    .map((slice) => ({
      name: slice.name,
      value: slice.value,
      color: slice.color,
    }));
}

/** Ranking demo: top productos por ingresos del último mes. */
function buildHorizontalBars(): HealthBarDatum[] {
  return CATEGORY_PERIOD_DATA.mes.topProducts
    .slice()
    .sort((a, b) => b.totalIngresos - a.totalIngresos)
    .slice(0, MAX_RANKING_ITEMS)
    .map((product) => ({
      label: product.nombre,
      value: product.totalIngresos,
    }));
}

/** Comparación demo: ventas por día de la semana (caja + tienda) del último mes. */
function buildVerticalBars(): HealthBarDatum[] {
  return WEEKDAY_SALES_MOCK_DATA.mes.map((day) => ({
    label: day.shortName,
    value: day.caja,
    secondary: day.tienda,
  }));
}

export const BUSINESS_HEALTH_MOCK: BusinessHealthData = {
  indicators: [
    {
      id: "ventas",
      label: "Ventas",
      status: "bueno",
      valueLabel: "$1.117.176",
      detail: "112% de la meta del día",
      progress: 100,
      path: "/caja/ventas",
    },
    {
      id: "rentabilidad",
      label: "Rentabilidad",
      status: "bueno",
      valueLabel: "34,4%",
      detail: "Margen bruto del mes",
      progress: 34,
      path: "/metricas/rentabilidad",
    },
    {
      id: "gastos",
      label: "Gastos",
      status: "atencion",
      valueLabel: "$412.300",
      detail: "37% de las ventas de hoy",
      progress: 37,
      path: "/metricas/resultados",
    },
    {
      id: "inventario",
      label: "Inventario",
      status: "critico",
      valueLabel: "18 con stock bajo",
      detail: "18 de 64 productos",
      progress: 28,
      path: "/inventario?stock=bajo",
    },
    {
      id: "clientes",
      label: "Clientes",
      status: "bueno",
      valueLabel: "6 nuevos",
      detail: "Clientes registrados hoy",
      progress: 100,
      path: "/caja/fidelizacion",
    },
    {
      id: "equipo",
      label: "Equipo",
      status: "bueno",
      valueLabel: "6/6 en planta",
      detail: "Empleados activos hoy",
      progress: 100,
      path: "/personal",
    },
    {
      id: "operacion",
      label: "Operación",
      status: "bueno",
      valueLabel: "1,8%",
      detail: "Cancelaciones (30 días)",
      progress: 18,
      path: "/metricas/operacion",
    },
    {
      id: "caja",
      label: "Caja",
      status: "atencion",
      valueLabel: "1 con diferencia",
      detail: "de 8 cierres · últimos 30 días",
      progress: 13,
      path: "/caja/cierres",
    },
  ],
  treemap: buildTreemap(),
  treemapSubtitle: "Unidades vendidas por categoría — último mes",
  verticalBars: buildVerticalBars(),
  verticalTitle: "Ventas por día de la semana",
  verticalSubtitle: "Acumulado — último mes",
  verticalPrimaryLabel: "Caja",
  verticalSecondaryLabel: "Web",
  horizontalBars: buildHorizontalBars(),
  horizontalTitle: "Top productos",
  horizontalSubtitle: "Ranking por ingresos — último mes",
  isDemo: true,
};
